import { Component } from '@theme/component';
import { getZonedParts, isValidTimeZone, zonedTimeToDate } from '@theme/zoned-time';

/**
 * @typedef {object} ShipsTimerRefs
 * @property {HTMLElement} text - Element the rendered text goes to.
 * @extends Component<ShipsTimerRefs>
 */

/** @type {Record<string, Intl.DateTimeFormatOptions>} */
const DATE_FORMATS = {
  month_day: { month: 'long', day: 'numeric' },
  weekday_month_day: { weekday: 'long', month: 'long', day: 'numeric' },
  short_month_day: { month: 'short', day: 'numeric' },
};

const DEFAULT_TIME_ZONE = 'America/New_York';
const DEFAULT_CUTOFF_HOUR = 16;

/**
 * @typedef {object} Shipping
 * @property {Date} cutoff - Moment the current shipping window closes.
 * @property {boolean} isToday - Whether the order still ships today (in the warehouse time zone).
 */

/**
 * Shipping window for `now`: orders placed before `cutoffHour` (warehouse time) ship
 * the same day, later ones ship the next day.
 * @param {Date} now
 * @param {string} timeZone - IANA time zone of the warehouse, e.g. "America/New_York".
 * @param {number} cutoffHour - Hour (0–23) in that time zone.
 * @returns {Shipping}
 */
export function getShipping(now, timeZone, cutoffHour) {
  const today = getZonedParts(now, timeZone);
  const todayCutoff = zonedTimeToDate(today.year, today.month, today.day, cutoffHour, timeZone);

  if (now < todayCutoff) {
    return { cutoff: todayCutoff, isToday: true };
  }

  // Date.UTC normalizes the overflowing day (e.g. Sep 31 → Oct 1).
  const tomorrow = new Date(Date.UTC(today.year, today.month - 1, today.day + 1));
  const tomorrowCutoff = zonedTimeToDate(
    tomorrow.getUTCFullYear(),
    tomorrow.getUTCMonth() + 1,
    tomorrow.getUTCDate(),
    cutoffHour,
    timeZone
  );

  return { cutoff: tomorrowCutoff, isToday: false };
}

/**
 * Shipping countdown: "Ships today, September 30 if ordered within 2hr 18min 44sec".
 */
class ShipsTimer extends Component {
  requiredRefs = ['text'];

  /** @type {number | undefined} */
  #interval;

  /** @type {Shipping | undefined} */
  #shipping;

  connectedCallback() {
    super.connectedCallback();

    this.timeZone = isValidTimeZone(this.dataset.timeZone) ? this.dataset.timeZone : DEFAULT_TIME_ZONE;

    const cutoffHour = Number.parseInt(this.dataset.cutoffHour ?? '', 10);
    this.cutoffHour = Number.isNaN(cutoffHour) ? DEFAULT_CUTOFF_HOUR : cutoffHour;

    // The locale is pinned so the date never depends on the browser language,
    // the time zone so the date is the shipping day of the warehouse.
    this.dateFormatter = new Intl.DateTimeFormat('en-US', {
      ...(DATE_FORMATS[this.dataset.dateFormat || ''] ?? DATE_FORMATS.month_day),
      timeZone: this.timeZone,
    });

    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#interval);
  }

  #tick() {
    const now = new Date();

    if (!this.#shipping || now >= this.#shipping.cutoff) {
      this.#shipping = getShipping(now, this.timeZone, this.cutoffHour);
    }

    this.#render(now, this.#shipping);
  }

  /**
   * @param {Date} now
   * @param {Shipping} shipping
   */
  #render(now, { cutoff, isToday }) {
    const template = (isToday ? this.dataset.template : this.dataset.templateTomorrow) || this.dataset.template || '';
    // The cutoff is on the shipping day in the warehouse time zone.
    const date = this.dateFormatter.format(cutoff);
    const left = cutoff.getTime() - now.getTime();
    // With the short mobile format both variants are rendered, CSS shows one of them.
    const countdownText =
      this.dataset.shortCountdownMobile === 'true'
        ? `<span class="sfv6-timer__long">${formatCountdown(left)}</span>` +
          `<span class="sfv6-timer__short">${formatCountdown(left, true)}</span>`
        : formatCountdown(left);
    const countdown = `<span class="sfv6-timer__countdown">${countdownText}</span>`;

    const html = escapeHtml(template)
      .replace(/\[([^\]]*)\]/g, '<strong>$1</strong>')
      .replace('{date}', date)
      .replace('{countdown}', countdown);

    if (this.refs.text.innerHTML !== html) {
      this.refs.text.innerHTML = html;
    }
  }
}

/**
 * @param {number} ms
 * @param {boolean} [short] - Use the compact units.
 * @returns {string} e.g. "2hr 18min 44sec", short: "2h 18m 44s"
 */
function formatCountdown(ms, short = false) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return short ? `${hours}h ${minutes}m ${seconds}s` : `${hours}hr ${minutes}min ${seconds}sec`;
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;

  return div.innerHTML;
}

if (!customElements.get('ships-timer')) {
  customElements.define('ships-timer', ShipsTimer);
}
